import { useEffect, useState } from 'react';

/** Estado de uma requisição assíncrona, no estilo "data / loading / error". */
export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  /** Incrementa para refazer a busca. */
  reload: () => void;
}

/**
 * Hook genérico para buscar dados uma vez (e sob demanda). Cancela a requisição
 * pendente no unmount/reload via AbortController.
 */
export function useAsync<T>(fetcher: (signal: AbortSignal) => Promise<T>): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);
    fetcher(controller.signal)
      .then((result) => {
        if (active) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
    // `fetcher` é estável (funções de módulo); reexecuta apenas em reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
